// The OCS Charter for Accountable AI (draft 1, docs/13 "The Charter"): what we ask AI companies to
// meet, in the order the articles argue for it. Signatures are counted by /api/charter.

export interface Principle {
  n: number;
  title: string;
  /** One line for cards. */
  line: string;
  body: string;
  /** Where the site argues it or puts it to work. */
  more: { to: string; label: string };
}

export const CHARTER: Principle[] = [
  {
    n: 1,
    title: "No one marks their own homework",
    line: "Agents acting for people are supervised and audited by parties independent of the vendor.",
    body: "The company that profits from an agent doing more cannot be the one deciding how much it may do, or the only one checking what it did. Oversight belongs to the people the agent acts for, and to independent bodies they trust.",
    more: { to: "/blog/the-keymaker-2-0", label: "Read: The Keymaker 2.0" },
  },
  {
    n: 2,
    title: "Show the meter before the run",
    line: "People see an estimated cost, and can set a cap, before a prompt runs. Bills are itemised.",
    body: "Nobody would buy a kettle whose power use is only revealed after it boils. AI is becoming a utility; it should be metered like one: a range before you send, a limit you choose, and a bill you can check.",
    more: { to: "/blog/marsellus-wallaces-briefcase", label: "Read: Marsellus Wallace's Briefcase" },
  },
  {
    n: 3,
    title: "Keys stay with their owners",
    line: "Agents act through brokers their owners control; they never hold the owner's credentials.",
    body: "An agent that holds your key can do anything you can. A broker you control makes each call for it, inside a list you wrote, and keeps a record of every use. You are the keymaker.",
    more: { to: "/products#broker", label: "OCS Broker" },
  },
  {
    n: 4,
    title: "Danger has a scale",
    line: "Every agent action is classed on an independent, public danger scale; the dangerous ones need a human.",
    body: "Reading a public page and wiring money are not the same kind of act. A public scale, kept by people who do not sell agents, says which actions an agent may take alone and which need a person to say yes.",
    more: { to: "/tools/danger-scale", label: "The Danger Scale (draft)" },
  },
  {
    n: 5,
    title: "Private by default",
    line: "Conversations and data are encrypted to their owners; providers and hosts see only what they must.",
    body: "Work done by agents is still your work, and your customers' data is still theirs. It should be encrypted to the people it belongs to, with the host holding ciphertext and the metadata it needs to run, nothing more.",
    more: { to: "/learn#private", label: "How to keep it private" },
  },
  {
    n: 6,
    title: "Workflows are portable",
    line: "Automations are written in open formats any platform can run. No lock-in.",
    body: "The steps, guards and approvals an organisation learns are its knowledge. They should live in an open, versioned document that anyone can read before running and take elsewhere after.",
    more: { to: "/portable-workflows", label: "Portable workflows" },
  },
  {
    n: 7,
    title: "People evolve with the work",
    line: "Organisations that automate invest in the people the automation affects.",
    body: "AI arrives either way. What is not inevitable is who carries the cost. Teams that automate owe the people affected a say in how, the time to learn to direct the new tools, and a share of what it saves.",
    more: { to: "/blog/i-am-inevitable", label: "Read: I Am Inevitable" },
  },
];
